import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import './CookieConsent.css';

export default function CookieConsent() {
  const [showBanner, setShowBanner] = useState(false);
  const [showPreferences, setShowPreferences] = useState(false);
  const [preferences, setPreferences] = useState({
    necessary: true, // Always true and disabled
    functional: false,
    analytics: false,
    marketing: false
  });

  useEffect(() => {
    // Check if user has already made a choice
    const consent = localStorage.getItem('cookie_consent');
    if (!consent) {
      setShowBanner(true);
    } else {
      // Load saved preferences
      try {
        const savedPreferences = JSON.parse(consent);
        setPreferences(savedPreferences);
      } catch (e) {
        console.error('Error parsing cookie preferences:', e);
        setShowBanner(true);
      }
    }
  }, []);

  const handleAcceptAll = () => {
    const allAccepted = {
      necessary: true,
      functional: true,
      analytics: true,
      marketing: true
    };
    savePreferences(allAccepted);
  };

  const handleRejectAll = () => {
    const allRejected = {
      necessary: true, // Always true
      functional: false,
      analytics: false,
      marketing: false
    };
    savePreferences(allRejected);
  };

  const handleAcceptSelected = () => {
    savePreferences(preferences);
  };

  const savePreferences = (selectedPreferences) => {
    localStorage.setItem('cookie_consent', JSON.stringify(selectedPreferences));
    setShowBanner(false);
    setShowPreferences(false);
    
    // Trigger event for analytics tools to respect user choice
    window.dispatchEvent(new CustomEvent('cookiePreferencesUpdated', {
      detail: selectedPreferences
    }));
  };

  if (!showBanner) return null;

  return (
    <div className="cookie-consent">
      {!showPreferences ? (
        <div className="cookie-banner">
          <div className="cookie-content">
            <h3>Szanujemy Twoją prywatność</h3>
            <p>
              Zgodnie z art. 173 Prawa Telekomunikacyjnego i RODO informujemy, że używamy plików cookie i podobnych technologii w następujących celach:
            </p>
            <ul>
              <li>Niezbędne - umożliwiające korzystanie z naszych usług</li>
              <li>Funkcjonalne - zapamiętywanie Twoich preferencji i ustawień</li>
              <li>Analityczne - pomagające nam zrozumieć, jak używasz naszej strony</li>
              <li>Marketingowe - pozwalające nam i naszym partnerom pokazywać Ci trafniejsze reklamy</li>
            </ul>
            <p>
              Szczegółowe informacje znajdziesz w naszej{' '}
              <Link to="/privacy#cookies">Polityce Prywatności</Link>.
            </p>
          </div>
          <div className="cookie-buttons">
            <button onClick={handleRejectAll} className="reject-button">
              Odrzuć opcjonalne
            </button>
            <button onClick={() => setShowPreferences(true)} className="preferences-button">
              Ustawienia
            </button>
            <button onClick={handleAcceptAll} className="accept-button">
              Akceptuj wszystkie
            </button>
          </div>
        </div>
      ) : (
        <div className="cookie-preferences">
          <h3>Ustawienia plików cookie</h3>
          <p className="preferences-info">
            Poniżej możesz dostosować swoje preferencje dotyczące plików cookie. Zgodnie z RODO, masz prawo do zmiany tych ustawień w dowolnym momencie.
          </p>
          <div className="preferences-options">
            <label className="preference-item">
              <div className="preference-header">
                <input
                  type="checkbox"
                  checked={preferences.necessary}
                  disabled
                  onChange={() => {}}
                />
                <div>
                  <strong>Niezbędne (wymagane)</strong>
                  <span className="cookie-duration">Czas przechowywania: sesja/30 dni</span>
                </div>
              </div>
              <p>Umożliwiają podstawowe funkcjonowanie strony: logowanie, koszyk zakupowy, sesję użytkownika, zabezpieczenia. Obejmują dane takie jak token uwierzytelniający, ID użytkownika oraz podstawowe ustawienia bezpieczeństwa. Są niezbędne do działania serwisu i nie można ich wyłączyć.</p>
            </label>
            <label className="preference-item">
              <div className="preference-header">
                <input
                  type="checkbox"
                  checked={preferences.functional}
                  onChange={(e) => setPreferences({...preferences, functional: e.target.checked})}
                />
                <div>
                  <strong>Funkcjonalne</strong>
                  <span className="cookie-duration">Czas przechowywania: 6 miesięcy</span>
                </div>
              </div>
              <p>Zapamiętują Twoje preferencje zakupowe i ustawienia: ostatnio przeglądane produkty, dane adresowe, preferowane metody dostawy, automatyczne uzupełnianie formularzy. Pozwalają na spersonalizowanie Twojego doświadczenia zakupowego.</p>
            </label>
            <label className="preference-item">
              <div className="preference-header">
                <input
                  type="checkbox"
                  checked={preferences.analytics}
                  onChange={(e) => setPreferences({...preferences, analytics: e.target.checked})}
                />
                <div>
                  <strong>Analityczne</strong>
                  <span className="cookie-duration">Czas przechowywania: 26 miesięcy</span>
                </div>
              </div>
              <p>Pomagają nam zrozumieć, jak korzystasz ze sklepu. Zbierają informacje o odwiedzanych stronach, czasie spędzonym na stronie, urządzeniu i przeglądarce. Wykorzystujemy te dane do poprawy funkcjonalności i wydajności sklepu. Dane są zbierane anonimowo, chyba że jesteś zalogowany.</p>
            </label>
            <label className="preference-item">
              <div className="preference-header">
                <input
                  type="checkbox"
                  checked={preferences.marketing}
                  onChange={(e) => setPreferences({...preferences, marketing: e.target.checked})}
                />
                <div>
                  <strong>Marketingowe</strong>
                  <span className="cookie-duration">Czas przechowywania: 3 miesiące</span>
                </div>
              </div>
              <p>Używane do personalizacji reklam i treści marketingowych. Obejmują preferencje newsletterowe, historię zakupów i przeglądania. Pozwalają nam i naszym partnerom pokazywać Ci trafniejsze reklamy i rekomendacje produktów. Możesz w każdej chwili wycofać zgodę na te pliki cookie.</p>
            </label>
          </div>
          <div className="preferences-buttons">
            <button onClick={handleRejectAll} className="reject-button">
              Odrzuć opcjonalne
            </button>
            <button onClick={handleAcceptSelected} className="save-button">
              Zapisz wybrane
            </button>
            <button onClick={handleAcceptAll} className="accept-all-button">
              Akceptuj wszystkie
            </button>
          </div>
        </div>
      )}
    </div>
  );
} 